/**
 * src/lib/hooks/useDirectoryData.js — Directory & People domain hook.
 *
 * Owns: people, ports, departments state,
 *       port creation callback.
 *
 * Extracted from useAppData.js (Phase 81-01).
 */

import { useState, useCallback } from 'react';
import { fetchPorts, createPort } from '../api/crm';
import { fetchPeople } from '../api/people';
import { supabase } from '../supabase';
import { logAction, logResult, logMergeDecision } from '../observability';
import { logAppError } from '../errorReporter';
import { mergeWithOptimistic } from '../mergeWithOptimistic';
import { reconcileCollection } from '../offline/entityCache';
import { getOnline } from '../offline/networkStatus';

export function useDirectoryData({ offlineEnabled = false } = {}) {
  const [people, setPeople] = useState([]);
  const [ports, setPorts] = useState([]);
  const [departments, setDepartments] = useState([]);

  const handleCreatePort = useCallback(async (data) => {
    const cid = logAction('directory:createPort', { port_name: data.port_name });
    try {
      const result = await createPort(data);
      if (result?.success && result.record) {
        setPorts(prev => [...prev, result.record].sort((a, b) => (a.port_name || '').localeCompare(b.port_name || '')));
        logResult('directory:createPort', 'success', { correlationId: cid });
      }
      return result;
    } catch (e) {
      logAppError(e, 'useDirectoryData:createPort');
      logResult('directory:createPort', 'error', { correlationId: cid, error: e.message });
      return { success: false, error: e.message };
    }
  }, []);

  // ─── Fetch / Apply / Merge (Phase 81-02) ───────────────────
  const fetchAll = useCallback(async () => {
    const [people, ports, departments] = await Promise.all([
      fetchPeople().catch(e => { console.warn('[DirectoryData] People failed:', e.message); return []; }),
      fetchPorts().catch(e => { console.warn('[DirectoryData] Ports failed:', e.message); return []; }),
      supabase.from('departments').select('id, name, name_es, display_order').order('display_order').then(r => r.data || []).catch(e => { console.warn('[DirectoryData] Departments failed:', e.message); return []; }),
    ]);
    if (offlineEnabled) {
      const online = getOnline();
      const [pe, po, de] = await Promise.all([
        reconcileCollection('people', people, online),
        reconcileCollection('ports', ports, online),
        reconcileCollection('departments', departments, online),
      ]);
      return { people: pe, ports: po, departments: de };
    }
    return { people, ports, departments };
  }, [offlineEnabled]);

  const applyFetchResult = useCallback((data) => {
    setPeople(Array.isArray(data.people) ? data.people : []);
    setPorts(Array.isArray(data.ports) ? data.ports : []);
    setDepartments(Array.isArray(data.departments) ? data.departments : []);
  }, []);

  const mergeAll = useCallback((data, optimisticTimestamps, now) => {
    const merge = (setter, newData) => {
      if (!newData) return;
      setter(prev => {
        const { merged, anyChanged, expiredIds } = mergeWithOptimistic(prev, newData, optimisticTimestamps, now);
        for (const { id, age_ms } of expiredIds) logMergeDecision(id, 'ttl_expired', { age_ms });
        return anyChanged ? merged : prev;
      });
    };
    merge(setPeople, data.people);
    merge(setPorts, data.ports);
    merge(setDepartments, data.departments);
  }, []);

  return {
    people, setPeople,
    ports, setPorts,
    departments, setDepartments,
    handleCreatePort,
    fetchAll,
    applyFetchResult,
    mergeAll,
  };
}
